import { Component, HostListener, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import {
  ExternalCalendarEvent,
  PlannableUser,
  ResourceAssignment,
  ResourceAssignmentPayload,
  ResourceCategoryDef,
  assignmentColor,
  paletteColor,
  workStatusMeta,
  WORK_STATUSES
} from '../../../Models/resource-assignment.model';
import { ResourcePlanningService } from '../../../Services/resource-planning.service';
import { AuthService } from '../../../api-authorization/authorize.service';
import {
  AssignmentDialogComponent,
  AssignmentDialogData,
  AssignmentDialogResult
} from '../assignment-dialog/assignment-dialog.component';
import { PlanningAdminDialogComponent } from '../planning-admin-dialog/planning-admin-dialog.component';
import {
  AvailabilityDialogComponent,
  AvailabilityResult
} from '../availability-dialog/availability-dialog.component';

type ViewMode = 'week' | 'month' | 'year';

interface DragState {
  userId: string;
  startIdx: number;
  endIdx: number;
}

/** Ein darzustellender Termin-Balken (kann über mehrere Tage spannen). */
interface AssignmentBar {
  assignment: ResourceAssignment;
  leftPct: number;
  widthPct: number;
  top: number;
  lane: number;
  startIdx: number;
  endIdx: number;
  showLabel: boolean;
  color: string;
  textColor: string;
  statusColor: string;
  statusLabel: string;
  done: boolean;
  tooltip: string;
  timeLabel: string;
  conflict: boolean;
  dimmed: boolean;
  isMirror: boolean;
  approvalPending: boolean;
  approvalRejected: boolean;
}

type BarDragMode = 'move' | 'resize-start' | 'resize-end';

interface BarDragState {
  bar: AssignmentBar;
  mode: BarDragMode;
  grabUserId: string;
  grabIdx: number;
  curUserId: string;
  curIdx: number;
  moved: boolean;
  // Für die "mitfliegende" Darstellung: Pixel-Versatz gegenüber dem Greifpunkt.
  grabClientX: number;
  grabClientY: number;
  dxPx: number;
  dyPx: number;
}

/** Ein read-only Balken aus einem externen (geteilten) Kalender. */
interface ExternalBar {
  event: ExternalCalendarEvent;
  leftPct: number;
  widthPct: number;
  top: number;
  title: string;
  tooltip: string;
  showLabel: boolean;
  hidden: boolean;
}

@Component({
  selector: 'app-resource-timeline',
  templateUrl: './resource-timeline.component.html',
  styleUrls: ['./resource-timeline.component.css']
})
export class ResourceTimelineComponent implements OnInit {
  viewMode: ViewMode = 'week';
  anchor!: Date;             // Bezugsdatum (Wochenanfang / Monatsanfang / Jahresanfang)

  users: PlannableUser[] = [];
  assignments: ResourceAssignment[] = [];
  externalEvents: ExternalCalendarEvent[] = [];
  categories: ResourceCategoryDef[] = [];

  days: Date[] = [];         // für Woche/Monat
  months: Date[] = [];       // für Jahr
  rangeStart!: Date;
  rangeEnd!: Date;

  loading = false;
  drag: DragState | null = null;
  barDrag: BarDragState | null = null;
  private justDragged = false;
  // Manuelle Doppelklick-Erkennung am Balken (robuster als natives dblclick,
  // da das Verschieben das DOM zwischen zwei Klicks neu aufbauen kann).
  private lastBarDownId: number | null = null;
  private lastBarDownTime = 0;

  // Filter
  filterText = '';
  filterCategoryIds: number[] = [];
  filterSkillIds: number[] = [];

  // Einfärben nach: Kategorie | Kunde | Status
  colorMode: 'category' | 'customer' | 'status' = 'category';
  workStatuses = WORK_STATUSES;

  // Ausgeblendete Sync-Termine ein-/ausblenden.
  showHiddenExternal = false;

  // Jetzt-Linie (Position in % über die sichtbaren Tage, null wenn außerhalb)
  nowLinePct: number | null = null;

  // Vorberechnetes Balken-Layout je Benutzer (für Woche/Monat).
  private readonly laneHeight = 24;
  private readonly extLaneHeight = 16;
  userBars: { [userId: string]: AssignmentBar[] } = {};
  userExtBars: { [userId: string]: ExternalBar[] } = {};
  private rowHeights: { [userId: string]: number } = {};

  weekdayShort = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  monthShort = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

  constructor(
    private service: ResourcePlanningService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    public auth: AuthService
  ) { }

  ngOnInit(): void {
    this.anchor = this.startOfWeek(new Date());
    this.buildPeriod();
    this.loadAll();
  }

  get isSuperAdmin(): boolean {
    return this.auth.isSuperAdmin();
  }

  // ---- View-Umschaltung ----
  setView(mode: ViewMode): void {
    if (this.viewMode === mode) {
      return;
    }
    // Bezugsdatum des aktuellen Fokus (heute, falls sichtbar – sonst Mitte des
    // sichtbaren Bereichs). So landet man z.B. beim Wechsel Woche→Monat bei einer
    // über zwei Monate verteilten Woche im "richtigen" (aktuellen/mehrheitlichen) Monat.
    const focus = this.currentFocusDate();
    const weekRef = this.viewMode === 'year'
      ? focus
      : this.days[0] || this.anchor;
    this.viewMode = mode;
    if (mode === 'week') {
      // Enthält die aktuelle Ansicht heute, die Woche um heute zeigen.
      this.anchor = this.startOfWeek(this.isTodayInVisibleRange() ? new Date() : weekRef);
    } else if (mode === 'month') {
      this.anchor = new Date(focus.getFullYear(), focus.getMonth(), 1);
    } else {
      this.anchor = new Date(focus.getFullYear(), 0, 1);
    }
    this.buildPeriod();
    this.loadAssignments();
  }

  /** True, wenn "heute" im aktuell sichtbaren Zeitraum liegt. */
  private isTodayInVisibleRange(): boolean {
    const now = new Date();
    return now >= this.rangeStart && now < this.rangeEnd;
  }

  /**
   * Bezugsdatum für einen Ansichtswechsel: bevorzugt "heute" (wenn sichtbar),
   * sonst die Mitte des aktuell sichtbaren Zeitraums. Damit wird bei einer Woche,
   * die über zwei Monate reicht, der aktuelle bzw. mehrheitliche Monat gewählt.
   */
  private currentFocusDate(): Date {
    if (this.isTodayInVisibleRange()) {
      return new Date();
    }
    return new Date((this.rangeStart.getTime() + this.rangeEnd.getTime()) / 2);
  }

  prev(): void {
    this.shift(-1);
  }

  next(): void {
    this.shift(1);
  }

  today(): void {
    const now = new Date();
    if (this.viewMode === 'week') {
      this.anchor = this.startOfWeek(now);
    } else if (this.viewMode === 'month') {
      this.anchor = new Date(now.getFullYear(), now.getMonth(), 1);
    } else {
      this.anchor = new Date(now.getFullYear(), 0, 1);
    }
    this.buildPeriod();
    this.loadAssignments();
  }

  private shift(dir: number): void {
    if (this.viewMode === 'week') {
      this.anchor = this.addDays(this.anchor, dir * 7);
    } else if (this.viewMode === 'month') {
      this.anchor = new Date(this.anchor.getFullYear(), this.anchor.getMonth() + dir, 1);
    } else {
      this.anchor = new Date(this.anchor.getFullYear() + dir, 0, 1);
    }
    this.buildPeriod();
    this.loadAssignments();
  }

  get periodLabel(): string {
    if (this.viewMode === 'year') {
      return `${this.anchor.getFullYear()}`;
    }
    if (this.viewMode === 'month') {
      return `${this.monthShort[this.anchor.getMonth()]} ${this.anchor.getFullYear()}`;
    }
    const end = this.addDays(this.anchor, 6);
    return `${this.formatDate(this.anchor)} – ${this.formatDate(end)}`;
  }

  // ---- Periode aufbauen ----
  private buildPeriod(): void {
    if (this.viewMode === 'year') {
      this.months = Array.from({ length: 12 }, (_, m) => new Date(this.anchor.getFullYear(), m, 1));
      this.days = [];
      this.rangeStart = new Date(this.anchor.getFullYear(), 0, 1);
      this.rangeEnd = new Date(this.anchor.getFullYear() + 1, 0, 1);
    } else if (this.viewMode === 'month') {
      const year = this.anchor.getFullYear();
      const month = this.anchor.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      this.days = Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));
      this.rangeStart = new Date(year, month, 1);
      this.rangeEnd = new Date(year, month + 1, 1);
    } else {
      this.days = Array.from({ length: 7 }, (_, i) => this.addDays(this.anchor, i));
      this.rangeStart = new Date(this.anchor);
      this.rangeEnd = this.addDays(this.anchor, 7);
    }
  }

  // ---- Daten laden ----
  // Externe Termine werden zuerst geladen: dabei übernimmt der Server automatisch
  // "CON:"-markierte Sync-Termine als Einplanung. Erst danach die Einplanungen holen,
  // damit die frisch übernommenen Termine sofort erscheinen.
  private loadAll(): void {
    this.loading = true;
    this.service.getExternalEvents(this.rangeStart, this.rangeEnd).subscribe({
      next: external => {
        this.externalEvents = external || [];
        forkJoin({
          users: this.service.getPlannableUsers(),
          assignments: this.service.getAssignments(this.rangeStart, this.rangeEnd),
          categories: this.service.getCategories()
        }).subscribe({
          next: res => {
            this.users = res.users || [];
            this.assignments = res.assignments || [];
            this.categories = res.categories || [];
            this.rebuildLayout();
            this.loading = false;
          },
          error: () => {
            this.snackBar.open('Daten konnten nicht geladen werden.', 'OK', { duration: 4000 });
            this.loading = false;
          }
        });
      },
      error: () => {
        this.snackBar.open('Daten konnten nicht geladen werden.', 'OK', { duration: 4000 });
        this.loading = false;
      }
    });
  }

  private loadAssignments(): void {
    this.loading = true;
    this.service.getExternalEvents(this.rangeStart, this.rangeEnd).subscribe({
      next: external => {
        this.externalEvents = external || [];
        this.service.getAssignments(this.rangeStart, this.rangeEnd).subscribe({
          next: assignments => {
            this.assignments = assignments || [];
            this.rebuildLayout();
            this.loading = false;
          },
          error: () => { this.loading = false; }
        });
      },
      error: () => { this.loading = false; }
    });
  }

  reloadUsersAndAssignments(): void {
    this.loadAll();
  }

  // ---- Termine je Zelle ----
  assignmentsFor(userId: string, day: Date): ResourceAssignment[] {
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0);
    const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59);
    return this.assignments
      .filter(a => a.assignedUserId === userId || (a.participantUserIds || []).includes(userId))
      .filter(a => {
        const s = new Date(a.start);
        const e = new Date(a.end);
        return s <= dayEnd && e >= dayStart;
      })
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  }

  colorFor(a: ResourceAssignment): string {
    return assignmentColor(a);
  }

  private barColor(a: ResourceAssignment): string {
    if (this.colorMode === 'status') {
      return workStatusMeta(a.workStatus).color;
    }
    if (this.colorMode === 'customer') {
      return paletteColor(a.customerName || (a.customerId != null ? 'c' + a.customerId : null));
    }
    return assignmentColor(a);
  }

  setColorMode(mode: 'category' | 'customer' | 'status'): void {
    this.colorMode = mode;
    this.rebuildLayout();
  }

  // ---- Balken-Layout (durchgehende mehrtägige Balken) ----
  get minGridWidth(): number {
    const n = this.days.length || 1;
    const colMin = this.viewMode === 'month' ? 34 : 110;
    return 220 + n * colMin;
  }

  rowHeight(userId: string): number {
    return this.rowHeights[userId] || (this.laneHeight + 14);
  }

  private rebuildLayout(): void {
    this.userBars = {};
    this.userExtBars = {};
    this.rowHeights = {};
    const n = this.days.length;
    if (n === 0 || this.viewMode === 'year') {
      return;
    }

    const firstDay = new Date(this.days[0].getFullYear(), this.days[0].getMonth(), this.days[0].getDate()).getTime();

    // Übernommene externe Termine ausblenden: Schlüssel = Benutzer|UID|Startzeit.
    const adoptedKeys = new Set<string>(
      this.assignments
        .filter(a => !!a.sourceExternalUid)
        .map(a => `${a.assignedUserId}|${a.sourceExternalUid}|${new Date(a.start).getTime()}`)
    );

    for (const user of this.users) {
      const userAssignments = this.assignments.filter(a =>
        a.assignedUserId === user.id || (a.participantUserIds || []).includes(user.id));
      const conflictIds = this.computeConflicts(userAssignments);

      const items = userAssignments
        .map(a => {
          const s = new Date(a.start);
          const e = new Date(a.end);
          let startIdx = this.dayIndexFrom(firstDay, s);
          let endIdx = this.dayIndexFrom(firstDay, e);
          // Endet ein Termin exakt um Mitternacht, deckt er den Vortag ab.
          if (e.getHours() === 0 && e.getMinutes() === 0 && e.getSeconds() === 0 && endIdx > startIdx) {
            endIdx -= 1;
          }
          return { a, startIdx, endIdx };
        })
        .filter(x => x.endIdx >= 0 && x.startIdx <= n - 1)
        .map(x => ({ a: x.a, startIdx: Math.max(0, x.startIdx), endIdx: Math.min(n - 1, x.endIdx) }))
        .sort((p, q) => p.startIdx - q.startIdx || q.endIdx - p.endIdx);

      const laneEnds: number[] = [];
      const bars: AssignmentBar[] = items.map(x => {
        let lane = 0;
        while (lane < laneEnds.length && laneEnds[lane] >= x.startIdx) {
          lane++;
        }
        laneEnds[lane] = x.endIdx;

        const span = x.endIdx - x.startIdx + 1;
        const conflict = conflictIds.has(x.a.id);
        const status = workStatusMeta(x.a.workStatus);
        const isMirror = x.a.assignedUserId !== user.id;
        const approvalPending = x.a.approvalStatus === 'Pending';
        const approvalRejected = x.a.approvalStatus === 'Rejected';
        return {
          assignment: x.a,
          leftPct: (x.startIdx / n) * 100,
          widthPct: (span / n) * 100,
          top: lane * this.laneHeight + 4,
          lane,
          startIdx: x.startIdx,
          endIdx: x.endIdx,
          showLabel: this.viewMode === 'week' || span >= 2,
          color: this.barColor(x.a),
          textColor: this.contrastText(this.barColor(x.a)),
          statusColor: status.color,
          statusLabel: status.label,
          done: status.value === 'Done',
          isMirror,
          timeLabel: this.timeLabel(x.a),
          tooltip: (isMirror ? '👥 Team · ' : '')
            + (approvalPending ? '⏳ Antrag offen · ' : '')
            + (approvalRejected ? '⛔ Abgelehnt · ' : '')
            + (conflict ? '⚠ Konflikt · ' : '') + x.a.title + ' · ' + this.timeLabel(x.a)
            + ' · ' + status.label
            + (x.a.customerName ? ' · ' + x.a.customerName : '')
            + (x.a.location ? ' · ' + x.a.location : '')
            + (x.a.maintenanceTitle ? ' · 🔧 ' + x.a.maintenanceTitle : '')
            + this.travelLabel(x.a)
            + ' · (Doppelklick: bearbeiten)',
          conflict,
          dimmed: this.filterActive && !this.matchesFilter(x.a),
          approvalPending,
          approvalRejected
        };
      });

      this.userBars[user.id] = bars;
      const assignmentLanes = Math.max(1, laneEnds.length);

      // Externe (geteilte) Kalendertermine als eigene Spuren unterhalb der Einplanungen.
      const extItems = this.externalEvents
        .filter(e => e.userId === user.id)
        .filter(e => !adoptedKeys.has(`${e.userId}|${e.uid}|${new Date(e.start).getTime()}`))
        // Ausgeblendete Sync-Termine nur zeigen, wenn ausdrücklich gewünscht.
        .filter(e => this.showHiddenExternal || !e.hidden)
        .map(e => {
          const s = new Date(e.start);
          const en = new Date(e.end);
          let startIdx = this.dayIndexFrom(firstDay, s);
          let endIdx = this.dayIndexFrom(firstDay, en);
          if (en.getHours() === 0 && en.getMinutes() === 0 && en.getSeconds() === 0 && endIdx > startIdx) {
            endIdx -= 1;
          }
          return { e, startIdx, endIdx };
        })
        .filter(x => x.endIdx >= 0 && x.startIdx <= n - 1)
        .map(x => ({ e: x.e, startIdx: Math.max(0, x.startIdx), endIdx: Math.min(n - 1, x.endIdx) }))
        .sort((p, q) => p.startIdx - q.startIdx || q.endIdx - p.endIdx);

      const extLaneEnds: number[] = [];
      const baseTop = assignmentLanes * this.laneHeight + 4;
      const extBars: ExternalBar[] = extItems.map(x => {
        let lane = 0;
        while (lane < extLaneEnds.length && extLaneEnds[lane] >= x.startIdx) {
          lane++;
        }
        extLaneEnds[lane] = x.endIdx;
        const span = x.endIdx - x.startIdx + 1;
        const timeText = x.e.allDay ? 'ganztägig' : this.eventTimeLabel(x.e);
        const hidden = !!x.e.hidden;
        return {
          event: x.e,
          leftPct: (x.startIdx / n) * 100,
          widthPct: (span / n) * 100,
          top: baseTop + lane * this.extLaneHeight,
          title: x.e.title,
          tooltip: (hidden ? '🚫 Ausgeblendet · ' : '📅 ') + x.e.title + ' · ' + timeText
            + (x.e.userName ? ' · ' + x.e.userName : '')
            + (hidden ? ' · (Klicken: wieder einblenden)' : ' · (Klicken: in Planung übernehmen)'),
          showLabel: this.viewMode === 'week' || span >= 2,
          hidden
        };
      });

      this.userExtBars[user.id] = extBars;
      const extLanes = extLaneEnds.length;
      this.rowHeights[user.id] = assignmentLanes * this.laneHeight + extLanes * this.extLaneHeight + 14;
    }

    this.computeNowLine();
  }

  private computeNowLine(): void {
    this.nowLinePct = null;
    const n = this.days.length;
    if (n === 0 || this.viewMode === 'year') {
      return;
    }
    const now = new Date();
    if (now < this.rangeStart || now >= this.rangeEnd) {
      return;
    }
    const firstDay = new Date(this.days[0].getFullYear(), this.days[0].getMonth(), this.days[0].getDate()).getTime();
    const idx = this.dayIndexFrom(firstDay, now);
    const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const frac = (now.getTime() - dayStart) / 86400000;
    this.nowLinePct = ((idx + frac) / n) * 100;
  }

  /** Liefert die IDs der Termine eines Benutzers, die sich zeitlich überschneiden. */
  private computeConflicts(userAssignments: ResourceAssignment[]): Set<number> {
    const ids = new Set<number>();
    for (let i = 0; i < userAssignments.length; i++) {
      for (let j = i + 1; j < userAssignments.length; j++) {
        const a = userAssignments[i];
        const b = userAssignments[j];
        if (a.categoryIsAbsence && b.categoryIsAbsence) {
          continue;
        }
        if (a.approvalStatus === 'Rejected' || b.approvalStatus === 'Rejected') {
          continue;
        }
        const aS = this.effStart(a);
        const aE = this.effEnd(a);
        const bS = this.effStart(b);
        const bE = this.effEnd(b);
        if (aS < bE && bS < aE) {
          ids.add(a.id);
          ids.add(b.id);
        }
      }
    }
    return ids;
  }

  // ---- Filter ----
  get filterActive(): boolean {
    return !!this.filterText.trim() || this.filterCategoryIds.length > 0 || this.filterSkillIds.length > 0;
  }

  private matchesFilter(a: ResourceAssignment): boolean {
    const text = this.filterText.trim().toLowerCase();
    if (text) {
      const hay = `${a.title} ${a.customerName || ''} ${a.location || ''} ${a.notes || ''}`.toLowerCase();
      if (!hay.includes(text)) {
        return false;
      }
    }
    if (this.filterCategoryIds.length > 0) {
      if (a.categoryId == null || !this.filterCategoryIds.includes(a.categoryId)) {
        return false;
      }
    }
    return true;
  }

  /** Zeile ausgrauen, wenn ein Skill-Filter aktiv ist und der Benutzer keinen davon hat. */
  isUserDimmed(user: PlannableUser): boolean {
    if (this.filterSkillIds.length === 0) {
      return false;
    }
    const userSkillIds = (user.skills || []).map(s => s.id);
    return !this.filterSkillIds.some(id => userSkillIds.includes(id));
  }

  applyFilter(): void {
    this.rebuildLayout();
  }

  clearFilter(): void {
    this.filterText = '';
    this.filterCategoryIds = [];
    this.filterSkillIds = [];
    this.rebuildLayout();
  }

  allSkills(): { id: number; name: string; color: string }[] {
    const map = new Map<number, { id: number; name: string; color: string }>();
    for (const u of this.users) {
      for (const s of u.skills || []) {
        map.set(s.id, s);
      }
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }

  hasOverload(user: PlannableUser): boolean {
    return this.utilization(user) > 100;
  }

  // ---- Datum-Sprung ----
  get jumpDateValue(): string {
    const d = this.days.length ? this.days[0] : (this.anchor || new Date());
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`;
  }

  setJumpDate(value: string): void {
    if (!value) {
      return;
    }
    const parts = value.split('-').map(p => parseInt(p, 10));
    if (parts.length !== 3 || parts.some(isNaN)) {
      return;
    }
    const target = new Date(parts[0], parts[1] - 1, parts[2]);
    if (this.viewMode === 'week') {
      this.anchor = this.startOfWeek(target);
    } else if (this.viewMode === 'month') {
      this.anchor = new Date(target.getFullYear(), target.getMonth(), 1);
    } else {
      this.anchor = new Date(target.getFullYear(), 0, 1);
    }
    this.buildPeriod();
    this.loadAssignments();
  }

  private eventTimeLabel(e: ExternalCalendarEvent): string {
    const s = new Date(e.start);
    const en = new Date(e.end);
    return `${this.pad(s.getHours())}:${this.pad(s.getMinutes())}–${this.pad(en.getHours())}:${this.pad(en.getMinutes())}`;
  }

  private dayIndexFrom(firstDayMs: number, d: Date): number {
    const dd = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    return Math.round((dd - firstDayMs) / 86400000);
  }

  timeLabel(a: ResourceAssignment): string {
    const s = new Date(a.start);
    const e = new Date(a.end);
    return `${this.pad(s.getHours())}:${this.pad(s.getMinutes())}–${this.pad(e.getHours())}:${this.pad(e.getMinutes())}`;
  }

  // ---- Auslastung ----
  private capacityPerDay(user: PlannableUser): number {
    const weekly = user.weeklyCapacityHours && user.weeklyCapacityHours > 0 ? user.weeklyCapacityHours : 40;
    return weekly / 5;
  }

  /** Effektiver Start inkl. Reisezeit-Puffer (Anfahrt). */
  private effStart(a: ResourceAssignment): number {
    return new Date(a.start).getTime() - (a.travelMinutesBefore || 0) * 60000;
  }

  /** Effektives Ende inkl. Reisezeit-Puffer (Rückfahrt). */
  private effEnd(a: ResourceAssignment): number {
    return new Date(a.end).getTime() + (a.travelMinutesAfter || 0) * 60000;
  }

  hasTravel(a: ResourceAssignment): boolean {
    return (a.travelMinutesBefore || 0) > 0 || (a.travelMinutesAfter || 0) > 0;
  }

  private travelLabel(a: ResourceAssignment): string {
    if (!this.hasTravel(a)) {
      return '';
    }
    return ` · 🚗 Reise ${a.travelMinutesBefore || 0}/${a.travelMinutesAfter || 0} Min.`;
  }

  private hoursOnDay(a: ResourceAssignment, day: Date): number {
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0).getTime();
    const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1, 0, 0, 0).getTime();
    const s = this.effStart(a);
    const e = this.effEnd(a);
    const overlap = Math.min(e, dayEnd) - Math.max(s, dayStart);
    return overlap > 0 ? overlap / 3600000 : 0;
  }

  /** Auslastung in % für einen Benutzer über einen Tagesbereich. */
  private utilizationOver(user: PlannableUser, start: Date, endExclusive: Date):
    { percent: number; urlaub: boolean; booked: number; available: number } {
    const cap = this.capacityPerDay(user);
    let workingDays = 0;
    let urlaubDays = 0;
    let booked = 0;
    let hasUrlaub = false;

    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    while (cursor < endExclusive) {
      const dow = cursor.getDay();
      const isWorkday = dow >= 1 && dow <= 5;
      const dayAssignments = this.assignmentsFor(user.id, cursor)
        .filter(a => a.approvalStatus !== 'Rejected');
      const absence = dayAssignments.some(a => a.categoryIsAbsence);

      if (isWorkday) {
        workingDays++;
        if (absence) {
          urlaubDays++;
          hasUrlaub = true;
        }
      }

      // Gebuchte Stunden an allen Tagen zählen (auch am Wochenende), außer an
      // Abwesenheitstagen. Pro Tag auf die Tageskapazität gedeckelt.
      if (!absence) {
        let dayHours = 0;
        for (const a of dayAssignments) {
          dayHours += this.hoursOnDay(a, cursor);
        }
        booked += Math.min(dayHours, cap);
      }

      cursor.setDate(cursor.getDate() + 1);
    }

    const available = Math.max(0, (workingDays - urlaubDays) * cap);
    const percent = available > 0 ? Math.round((booked / available) * 100) : 0;
    return { percent, urlaub: hasUrlaub, booked, available };
  }

  utilization(user: PlannableUser): number {
    return this.utilizationOver(user, this.rangeStart, this.rangeEnd).percent;
  }

  utilizationTooltip(user: PlannableUser): string {
    const d = this.utilizationOver(user, this.rangeStart, this.rangeEnd);
    return `Auslastung im Zeitraum: ${d.booked.toFixed(1)} h geplant / ${d.available.toFixed(1)} h Kapazität`;
  }

  utilizationColor(percent: number): string {
    if (percent >= 100) { return '#c62828'; }
    if (percent >= 80) { return '#ef6c00'; }
    if (percent >= 40) { return '#2e7d32'; }
    if (percent > 0) { return '#1976d2'; }
    return '#cfd8dc';
  }

  // ---- Jahresansicht ----
  monthStats(user: PlannableUser, month: Date): { percent: number; count: number } {
    const start = new Date(month.getFullYear(), month.getMonth(), 1);
    const end = new Date(month.getFullYear(), month.getMonth() + 1, 1);
    const percent = this.utilizationOver(user, start, end).percent;
    const count = this.assignments.filter(a =>
      a.assignedUserId === user.id &&
      new Date(a.start) < end &&
      new Date(a.end) >= start
    ).length;
    return { percent, count };
  }

  openMonthFromYear(month: Date): void {
    this.viewMode = 'month';
    this.anchor = new Date(month.getFullYear(), month.getMonth(), 1);
    this.buildPeriod();
    this.loadAssignments();
  }

  // ---- Drag & Drop (Tagebereich markieren) ----
  onCellMouseDown(user: PlannableUser, dayIndex: number, event: MouseEvent): void {
    // Nur linke Maustaste und nur auf leerer Fläche (nicht auf einem Termin)
    if (event.button !== 0) {
      return;
    }
    this.drag = { userId: user.id, startIdx: dayIndex, endIdx: dayIndex };
  }

  onCellMouseEnter(user: PlannableUser, dayIndex: number): void {
    if (this.drag && this.drag.userId === user.id) {
      this.drag.endIdx = dayIndex;
    }
    if (this.barDrag) {
      if (this.barDrag.mode === 'move') {
        this.barDrag.curUserId = user.id;
      }
      if (this.barDrag.curIdx !== dayIndex || this.barDrag.curUserId !== user.id) {
        this.barDrag.moved = true;
      }
      this.barDrag.curIdx = dayIndex;
    }
  }

  isInDragSelection(userId: string, dayIndex: number): boolean {
    if (this.drag && this.drag.userId === userId) {
      const lo = Math.min(this.drag.startIdx, this.drag.endIdx);
      const hi = Math.max(this.drag.startIdx, this.drag.endIdx);
      if (dayIndex >= lo && dayIndex <= hi) {
        return true;
      }
    }
    return this.isBarDragTarget(userId, dayIndex);
  }

  private isBarDragTarget(userId: string, dayIndex: number): boolean {
    const bd = this.barDrag;
    if (!bd || !bd.moved) {
      return false;
    }
    if (bd.mode === 'move') {
      if (userId !== bd.curUserId) {
        return false;
      }
      const delta = bd.curIdx - bd.grabIdx;
      const lo = bd.bar.startIdx + delta;
      const hi = bd.bar.endIdx + delta;
      return dayIndex >= Math.min(lo, hi) && dayIndex <= Math.max(lo, hi);
    }
    if (userId !== bd.grabUserId) {
      return false;
    }
    if (bd.mode === 'resize-end') {
      return dayIndex >= bd.bar.startIdx && dayIndex <= Math.max(bd.bar.startIdx, bd.curIdx);
    }
    return dayIndex >= Math.min(bd.bar.endIdx, bd.curIdx) && dayIndex <= bd.bar.endIdx;
  }

  // ---- Balken verschieben / Größe ändern ----
  isBarDragging(): boolean {
    return !!this.barDrag;
  }

  onBarMouseDown(bar: AssignmentBar, user: PlannableUser, event: MouseEvent): void {
    if (event.button !== 0) {
      return;
    }
    event.stopPropagation();

    // Doppelklick auf denselben Termin (kurz hintereinander) → Bearbeiten öffnen.
    const now = Date.now();
    if (this.lastBarDownId === bar.assignment.id && now - this.lastBarDownTime < 400) {
      this.lastBarDownId = null;
      this.barDrag = null;
      this.justDragged = false;
      this.openEdit(bar.assignment, event);
      return;
    }
    this.lastBarDownId = bar.assignment.id;
    this.lastBarDownTime = now;

    // Team-Spiegel (Termin gehört primär einem anderen) nicht per Drag verschieben.
    if (bar.isMirror) {
      return;
    }
    this.justDragged = false;
    const track = (event.currentTarget as HTMLElement).closest('.track') as HTMLElement | null;
    const grabIdx = track ? this.idxFromClientX(track, event.clientX) : bar.startIdx;
    this.barDrag = {
      bar, mode: 'move', grabUserId: user.id, grabIdx,
      curUserId: user.id, curIdx: grabIdx, moved: false,
      grabClientX: event.clientX, grabClientY: event.clientY, dxPx: 0, dyPx: 0
    };
  }

  onBarHandleMouseDown(bar: AssignmentBar, user: PlannableUser, side: 'start' | 'end', event: MouseEvent): void {
    if (event.button !== 0 || bar.isMirror) {
      return;
    }
    event.stopPropagation();
    this.justDragged = false;
    const idx = side === 'start' ? bar.startIdx : bar.endIdx;
    this.barDrag = {
      bar, mode: side === 'start' ? 'resize-start' : 'resize-end',
      grabUserId: user.id, grabIdx: idx, curUserId: user.id, curIdx: idx, moved: false,
      grabClientX: event.clientX, grabClientY: event.clientY, dxPx: 0, dyPx: 0
    };
  }

  private idxFromClientX(track: HTMLElement, clientX: number): number {
    const rect = track.getBoundingClientRect();
    const n = this.days.length || 1;
    const colW = rect.width / n;
    const i = Math.floor((clientX - rect.left) / colW);
    return Math.max(0, Math.min(n - 1, i));
  }

  // Der gezogene Balken "fliegt" 1:1 mit dem Mauszeiger mit (Pixel-Versatz),
  // während die Zielspalte darunter hervorgehoben wird.
  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    if (!this.barDrag) {
      return;
    }
    const bd = this.barDrag;
    bd.dxPx = event.clientX - bd.grabClientX;
    // Vertikales Mitfliegen nur beim Verschieben, nicht beim Größe-Ändern.
    bd.dyPx = bd.mode === 'move' ? event.clientY - bd.grabClientY : 0;
    // Etwas größerer Schwellwert, damit ein leicht wackelnder Doppelklick nicht
    // versehentlich als Verschieben gewertet wird.
    if (Math.abs(bd.dxPx) > 6 || Math.abs(bd.dyPx) > 6) {
      bd.moved = true;
    }
  }

  /** Ist dieser Balken gerade der (mitfliegende) gezogene Balken? */
  isDraggedBar(bar: AssignmentBar): boolean {
    return !!this.barDrag && this.barDrag.bar === bar;
  }

  /** Live-Transform für den mitfliegenden Balken. */
  draggedBarTransform(bar: AssignmentBar): string | null {
    if (!this.barDrag || this.barDrag.bar !== bar || !this.barDrag.moved) {
      return null;
    }
    return `translate(${this.barDrag.dxPx}px, ${this.barDrag.dyPx}px)`;
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    if (this.barDrag) {
      const bd = this.barDrag;
      this.barDrag = null;
      if (bd.moved) {
        this.justDragged = true;
        this.applyBarDrag(bd);
      }
      return;
    }

    if (!this.drag) {
      return;
    }
    const { userId, startIdx, endIdx } = this.drag;
    this.drag = null;
    const lo = Math.min(startIdx, endIdx);
    const hi = Math.max(startIdx, endIdx);
    const user = this.users.find(u => u.id === userId);
    if (!user) {
      return;
    }
    const startDay = this.days[lo];
    const endDay = this.days[hi];
    const start = new Date(startDay.getFullYear(), startDay.getMonth(), startDay.getDate(), 8, 0, 0);
    const end = new Date(endDay.getFullYear(), endDay.getMonth(), endDay.getDate(), 17, 0, 0);
    this.openCreateRange(user, start, end);
  }

  // ---- Tastaturkürzel: ← / → blättern, T = heute, W/M/J = Ansicht ----
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // In Eingabefeldern/Dialogen keine Kürzel abfangen.
    const el = event.target as HTMLElement | null;
    const tag = el?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable) {
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    switch (event.key) {
      case 'ArrowLeft': this.prev(); break;
      case 'ArrowRight': this.next(); break;
      case 't': case 'T': this.today(); break;
      case 'w': case 'W': this.setView('week'); break;
      case 'm': case 'M': this.setView('month'); break;
      case 'j': case 'J': this.setView('year'); break;
      default: return;
    }
    event.preventDefault();
  }

  /** Wählt anhand der Balkenfarbe eine gut lesbare Textfarbe (schwarz/weiß). */
  private contrastText(bg: string | null | undefined): string {
    const hex = (bg || '').replace('#', '').trim();
    if (hex.length !== 3 && hex.length !== 6) {
      return '#ffffff';
    }
    const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
    const r = parseInt(full.substring(0, 2), 16);
    const g = parseInt(full.substring(2, 4), 16);
    const b = parseInt(full.substring(4, 6), 16);
    // Relative Helligkeit (YIQ) – über dem Schwellwert dunkler Text.
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 160 ? '#1f2a30' : '#ffffff';
  }

  private applyBarDrag(bd: BarDragState): void {
    const a = bd.bar.assignment;
    const n = this.days.length;
    let newStartIdx = bd.bar.startIdx;
    let newEndIdx = bd.bar.endIdx;
    let newUserId = a.assignedUserId;

    if (bd.mode === 'move') {
      const delta = bd.curIdx - bd.grabIdx;
      newStartIdx = bd.bar.startIdx + delta;
      newEndIdx = bd.bar.endIdx + delta;
      if (newStartIdx < 0) { newEndIdx -= newStartIdx; newStartIdx = 0; }
      if (newEndIdx > n - 1) { newStartIdx -= (newEndIdx - (n - 1)); newEndIdx = n - 1; }
      newStartIdx = Math.max(0, newStartIdx);
      newUserId = bd.curUserId;
    } else if (bd.mode === 'resize-end') {
      newEndIdx = Math.max(bd.bar.startIdx, Math.min(n - 1, bd.curIdx));
    } else {
      newStartIdx = Math.min(bd.bar.endIdx, Math.max(0, bd.curIdx));
    }

    if (newStartIdx === bd.bar.startIdx && newEndIdx === bd.bar.endIdx && newUserId === a.assignedUserId) {
      return;
    }

    const newStart = this.combineDateTime(this.days[newStartIdx], new Date(a.start));
    const newEnd = this.combineDateTime(this.days[newEndIdx], new Date(a.end));
    const payload = this.toPayload(a, { assignedUserId: newUserId, start: newStart, end: newEnd });

    const prev = { userId: a.assignedUserId, start: a.start, end: a.end };
    this.service.updateAssignment(a.id, payload).subscribe({
      next: () => {
        this.loadAssignments();
        this.showUndo('Termin verschoben.', () => {
          const undoPayload = this.toPayload(a, {
            assignedUserId: prev.userId,
            start: new Date(prev.start),
            end: new Date(prev.end)
          });
          this.service.updateAssignment(a.id, undoPayload).subscribe(() => this.loadAssignments());
        });
      },
      error: () => this.snackBar.open('Verschieben fehlgeschlagen.', 'OK', { duration: 4000 })
    });
  }

  private toPayload(
    a: ResourceAssignment,
    o: { assignedUserId?: string; start?: Date; end?: Date; sendInvite?: boolean }
  ): ResourceAssignmentPayload {
    return {
      assignedUserId: o.assignedUserId ?? a.assignedUserId,
      title: a.title,
      categoryId: a.categoryId ?? null,
      start: this.dateToIso(o.start ?? new Date(a.start)),
      end: this.dateToIso(o.end ?? new Date(a.end)),
      location: a.location || undefined,
      customerId: a.customerId ?? null,
      equipmentId: a.equipmentId ?? null,
      maintenanceId: a.maintenanceId ?? null,
      notes: a.notes || undefined,
      color: a.color || undefined,
      sourceExternalUid: a.sourceExternalUid ?? null,
      workStatus: a.workStatus || 'Planned',
      sendInvite: o.sendInvite ?? false
    };
  }

  private combineDateTime(day: Date, timeFrom: Date): Date {
    return new Date(day.getFullYear(), day.getMonth(), day.getDate(), timeFrom.getHours(), timeFrom.getMinutes(), 0);
  }

  private dateToIso(d: Date): string {
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}T${this.pad(d.getHours())}:${this.pad(d.getMinutes())}:00`;
  }

  private showUndo(label: string, run: () => void): void {
    const ref = this.snackBar.open(label, 'Rückgängig', { duration: 6000 });
    ref.onAction().subscribe(() => run());
  }

  // ---- Aktionen ----
  openCreateRange(user: PlannableUser, start: Date, end: Date): void {
    const data: AssignmentDialogData = {
      users: this.users,
      categories: this.categories,
      defaultUserId: user.id,
      defaultStart: start,
      defaultEnd: end
    };
    this.openDialogForCreate(data);
  }

  openCreate(user: PlannableUser, day: Date): void {
    const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 8, 0, 0);
    const end = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9, 0, 0);
    this.openCreateRange(user, start, end);
  }

  private openDialogForCreate(data: AssignmentDialogData, onSaved?: () => void): void {
    this.dialog.open(AssignmentDialogComponent, { data, autoFocus: false, panelClass: 'rp-dialog-panel', maxWidth: '96vw' })
      .afterClosed().subscribe((result?: AssignmentDialogResult) => {
        if (result?.action === 'save' && result.payload) {
          this.service.createAssignment(result.payload).subscribe({
            next: () => {
              this.snackBar.open(
                result.payload!.sendInvite ? 'Termin gespeichert & Einladung versendet.' : 'Termin gespeichert.',
                'OK', { duration: 3000 });
              if (onSaved) {
                onSaved();
              } else {
                this.loadAssignments();
              }
            },
            error: () => this.snackBar.open('Speichern fehlgeschlagen.', 'OK', { duration: 4000 })
          });
        }
      });
  }

  openEdit(assignment: ResourceAssignment, event: Event): void {
    event.stopPropagation();
    // Klick nach einem Drag (Verschieben/Resize) nicht als "Bearbeiten" werten.
    if (this.justDragged) {
      this.justDragged = false;
      return;
    }
    const data: AssignmentDialogData = { users: this.users, categories: this.categories, assignment };
    this.dialog.open(AssignmentDialogComponent, { data, autoFocus: false, panelClass: 'rp-dialog-panel', maxWidth: '96vw' })
      .afterClosed().subscribe((result?: AssignmentDialogResult) => {
        if (!result) {
          return;
        }
        if (result.action === 'save' && result.payload) {
          this.service.updateAssignment(assignment.id, result.payload).subscribe({
            next: () => {
              this.snackBar.open('Termin aktualisiert.', 'OK', { duration: 3000 });
              this.loadAssignments();
            },
            error: () => this.snackBar.open('Aktualisieren fehlgeschlagen.', 'OK', { duration: 4000 })
          });
        } else if (result.action === 'delete') {
          if (result.deleteSeries && assignment.seriesId) {
            this.service.deleteSeries(assignment.seriesId, !!result.sendCancellation).subscribe({
              next: () => {
                this.loadAssignments();
                this.snackBar.open('Serie gelöscht.', 'OK', { duration: 4000 });
              },
              error: () => this.snackBar.open('Löschen fehlgeschlagen.', 'OK', { duration: 4000 })
            });
            return;
          }
          const snapshot = this.toPayload(assignment, {});
          this.service.deleteAssignment(assignment.id, !!result.sendCancellation).subscribe({
            next: () => {
              this.loadAssignments();
              this.showUndo('Termin gelöscht.', () => {
                this.service.createAssignment(snapshot).subscribe(() => this.loadAssignments());
              });
            },
            error: () => this.snackBar.open('Löschen fehlgeschlagen.', 'OK', { duration: 4000 })
          });
        }
      });
  }

  // ---- Schnellaktionen am Balken ----
  advanceStatus(bar: AssignmentBar, event: Event): void {
    event.stopPropagation();
    this.justDragged = false;
    const a = bar.assignment;
    const order = WORK_STATUSES.map(w => w.value);
    const cur = order.indexOf((a.workStatus as any) || 'Planned');
    const next = order[(cur + 1) % order.length];
    const prev = a.workStatus || 'Planned';
    this.service.setStatus(a.id, next).subscribe({
      next: () => {
        this.loadAssignments();
        this.snackBar.open(`Status: ${workStatusMeta(next).label}`, 'Rückgängig', { duration: 4000 })
          .onAction().subscribe(() => this.service.setStatus(a.id, prev).subscribe(() => this.loadAssignments()));
      },
      error: () => this.snackBar.open('Status ändern fehlgeschlagen.', 'OK', { duration: 4000 })
    });
  }

  approveAbsence(bar: AssignmentBar, event: Event): void {
    event.stopPropagation();
    this.justDragged = false;
    this.service.approveAbsence(bar.assignment.id).subscribe({
      next: () => {
        this.loadAssignments();
        this.snackBar.open('Abwesenheit genehmigt.', 'OK', { duration: 3000 });
      },
      error: () => this.snackBar.open('Genehmigen fehlgeschlagen.', 'OK', { duration: 4000 })
    });
  }

  rejectAbsence(bar: AssignmentBar, event: Event): void {
    event.stopPropagation();
    this.justDragged = false;
    this.service.rejectAbsence(bar.assignment.id).subscribe({
      next: () => {
        this.loadAssignments();
        this.snackBar.open('Abwesenheit abgelehnt.', 'OK', { duration: 3000 });
      },
      error: () => this.snackBar.open('Ablehnen fehlgeschlagen.', 'OK', { duration: 4000 })
    });
  }

  duplicate(bar: AssignmentBar, event: Event): void {
    event.stopPropagation();
    this.justDragged = false;
    const payload = this.toPayload(bar.assignment, {});
    this.service.createAssignment(payload).subscribe({
      next: created => {
        this.loadAssignments();
        this.snackBar.open('Termin dupliziert.', 'Rückgängig', { duration: 5000 })
          .onAction().subscribe(() => this.service.deleteAssignment(created.id, false).subscribe(() => this.loadAssignments()));
      },
      error: () => this.snackBar.open('Duplizieren fehlgeschlagen.', 'OK', { duration: 4000 })
    });
  }

  sendInviteQuick(bar: AssignmentBar, event: Event): void {
    event.stopPropagation();
    this.justDragged = false;
    this.service.sendInvite(bar.assignment.id).subscribe({
      next: () => { this.snackBar.open('Einladung versendet.', 'OK', { duration: 3000 }); this.loadAssignments(); },
      error: () => this.snackBar.open('Versand fehlgeschlagen (SMTP prüfen).', 'OK', { duration: 4000 })
    });
  }

  /** Übernimmt einen externen (geteilten) Kalendertermin als normale Einplanung. */
  /** Klick auf einen Sync-Termin: ausgeblendete wieder einblenden, sonst übernehmen. */
  onExtBarClick(bar: ExternalBar, event: Event): void {
    if (bar.hidden) {
      this.unhideExternal(bar, event);
    } else {
      this.adoptExternal(bar, event);
    }
  }

  adoptExternal(bar: ExternalBar, event: Event): void {
    event.stopPropagation();
    const user = this.users.find(u => u.id === bar.event.userId);
    if (!user) {
      return;
    }
    const ev = bar.event;
    const data: AssignmentDialogData = {
      users: this.users,
      categories: this.categories,
      defaultUserId: user.id,
      defaultStart: new Date(ev.start),
      defaultEnd: new Date(ev.end),
      defaultTitle: ev.title,
      defaultNotes: 'Aus geteiltem Kalender übernommen',
      defaultSendInvite: false,
      defaultSourceUid: ev.uid
    };
    // Nach erfolgreicher Übernahme den Quell-Termin dauerhaft ausblenden
    // (unabhängig davon, ob der Startzeitpunkt im Dialog geändert wurde).
    this.openDialogForCreate(data, () => {
      this.service.hideExternalEvent(ev.userId, ev.uid, ev.start).subscribe({
        next: () => this.loadAssignments(),
        error: () => this.loadAssignments()
      });
    });
  }

  /** Blendet einen einzelnen Sync-Termin aus (ohne ihn zu übernehmen). */
  hideExternal(bar: ExternalBar, event: Event): void {
    event.stopPropagation();
    const ev = bar.event;
    this.service.hideExternalEvent(ev.userId, ev.uid, ev.start).subscribe({
      next: () => {
        this.snackBar.open('Sync-Termin ausgeblendet.', 'Rückgängig', { duration: 5000 })
          .onAction().subscribe(() =>
            this.service.unhideExternalEvent(ev.userId, ev.uid, ev.start).subscribe(() => this.loadAssignments()));
        this.loadAssignments();
      },
      error: () => this.snackBar.open('Ausblenden fehlgeschlagen.', 'OK', { duration: 4000 })
    });
  }

  /** Blendet einen zuvor ausgeblendeten Sync-Termin wieder ein. */
  unhideExternal(bar: ExternalBar, event: Event): void {
    event.stopPropagation();
    const ev = bar.event;
    this.service.unhideExternalEvent(ev.userId, ev.uid, ev.start).subscribe({
      next: () => {
        this.snackBar.open('Sync-Termin wieder eingeblendet.', 'OK', { duration: 3000 });
        this.loadAssignments();
      },
      error: () => this.snackBar.open('Einblenden fehlgeschlagen.', 'OK', { duration: 4000 })
    });
  }

  toggleHiddenExternal(): void {
    this.showHiddenExternal = !this.showHiddenExternal;
    this.rebuildLayout();
  }

  get hiddenExternalCount(): number {
    return this.externalEvents.filter(e => e.hidden).length;
  }

  openAvailability(): void {
    const defaultDate = this.days.length ? this.days[0] : new Date();
    this.dialog.open(AvailabilityDialogComponent, {
      data: { users: this.users, skills: this.allSkills(), defaultDate },
      autoFocus: false,
      width: '720px',
      maxWidth: '96vw',
      panelClass: 'rp-dialog-panel'
    }).afterClosed().subscribe((res?: AvailabilityResult) => {
      if (res) {
        const user = this.users.find(u => u.id === res.userId);
        if (user) {
          this.openCreateRange(user, res.start, res.end);
        }
      }
    });
  }

  print(): void {
    const html = this.buildPrintHtml();
    const win = window.open('', '_blank');
    if (!win) {
      this.snackBar.open('Druckfenster wurde blockiert. Bitte Pop-ups erlauben.', 'OK', { duration: 5000 });
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    // Kurz warten, bis das Layout steht, dann drucken.
    win.focus();
    setTimeout(() => { win.print(); }, 300);
  }

  private esc(v: any): string {
    return String(v ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private buildPrintHtml(): string {
    const style = `
      * { box-sizing: border-box; }
      body { font-family: Arial, Helvetica, sans-serif; color: #1a1a2e; margin: 0; padding: 12px; }
      h1 { font-size: 16px; margin: 0 0 2px 0; }
      .period { font-size: 12px; color: #555; margin-bottom: 10px; }
      table { border-collapse: collapse; width: 100%; table-layout: fixed; }
      th, td { border: 1px solid #cfd8dc; padding: 3px 4px; font-size: 10px; vertical-align: top; word-wrap: break-word; }
      th { background: #f0f3f7; }
      th.usercol, td.usercol { width: 130px; text-align: left; }
      td.usercol { font-weight: bold; }
      .wend { background: #f7f7f7; }
      .ev { border-left: 4px solid #1976d2; padding: 1px 3px; margin: 2px 0; border-radius: 2px; background: #fafafa; }
      .ev .t { font-weight: bold; }
      .ev .m { color: #555; }
      .util { font-weight: bold; text-align: center; color: #fff; }
      .legend { margin-top: 8px; font-size: 10px; color: #555; }
      @page { size: A4 landscape; margin: 8mm; }
    `;
    const header = `<h1>Ressourcenplanung</h1><div class="period">${this.esc(this.periodLabel)} · ${this.esc(this.viewModeLabel())} · gedruckt ${this.esc(this.formatDate(new Date()))}</div>`;
    const table = this.viewMode === 'year' ? this.buildPrintYear() : this.buildPrintDays();
    return `<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"><title>Ressourcenplanung</title><style>${style}</style></head><body>${header}${table}</body></html>`;
  }

  private viewModeLabel(): string {
    return this.viewMode === 'week' ? 'Woche' : this.viewMode === 'month' ? 'Monat' : 'Jahr';
  }

  private buildPrintDays(): string {
    let head = '<tr><th class="usercol">Mitarbeiter</th>';
    for (const d of this.days) {
      const wend = this.isWeekend(d) ? ' class="wend"' : '';
      head += `<th${wend}>${this.weekdayShort[d.getDay()]} ${this.pad(d.getDate())}.${this.pad(d.getMonth() + 1)}.</th>`;
    }
    head += '</tr>';

    let body = '';
    for (const user of this.users) {
      body += `<tr><td class="usercol">${this.esc(user.name)}<br><span style="font-weight:normal;color:#777">${this.utilization(user)}%</span></td>`;
      for (const d of this.days) {
        const wend = this.isWeekend(d) ? ' class="wend"' : '';
        const items = this.assignmentsFor(user.id, d);
        let cell = '';
        for (const a of items) {
          const mirror = a.assignedUserId !== user.id ? '👥 ' : '';
          const meta = a.customerName || a.location || '';
          cell += `<div class="ev" style="border-left-color:${this.esc(this.colorFor(a))}">`
            + `<span class="t">${mirror}${this.esc(a.title)}</span> <span class="m">${this.esc(this.timeLabel(a))}</span>`
            + (meta ? `<br><span class="m">${this.esc(meta)}</span>` : '')
            + `</div>`;
        }
        body += `<td${wend}>${cell}</td>`;
      }
      body += '</tr>';
    }
    return `<table>${head}${body}</table>`;
  }

  private buildPrintYear(): string {
    let head = '<tr><th class="usercol">Mitarbeiter</th>';
    for (const m of this.months) {
      head += `<th>${this.monthShort[m.getMonth()]}</th>`;
    }
    head += '</tr>';

    let body = '';
    for (const user of this.users) {
      body += `<tr><td class="usercol">${this.esc(user.name)}</td>`;
      for (const m of this.months) {
        const s = this.monthStats(user, m);
        const bg = s.count > 0 ? this.utilizationColor(s.percent) : '#ffffff';
        const txt = s.count > 0 ? `${s.percent}%` : '';
        body += `<td class="util" style="background:${bg}">${txt}</td>`;
      }
      body += '</tr>';
    }
    return `<table>${head}${body}</table>`;
  }

  openManagement(): void {
    this.dialog.open(PlanningAdminDialogComponent, { autoFocus: false, width: '860px', maxWidth: '96vw', panelClass: 'rp-dialog-panel' })
      .afterClosed().subscribe((changed?: boolean) => {
        if (changed) {
          this.loadAll();
        }
      });
  }

  // ---- Datums-Helfer ----
  isToday(day: Date): boolean {
    const t = new Date();
    return day.getFullYear() === t.getFullYear()
      && day.getMonth() === t.getMonth()
      && day.getDate() === t.getDate();
  }

  isWeekend(day: Date): boolean {
    const d = day.getDay();
    return d === 0 || d === 6;
  }

  private startOfWeek(d: Date): Date {
    const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const day = (date.getDay() + 6) % 7; // Montag = 0
    date.setDate(date.getDate() - day);
    return date;
  }

  private addDays(d: Date, n: number): Date {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
  }

  private formatDate(d: Date): string {
    return `${this.pad(d.getDate())}.${this.pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  }

  private pad(n: number): string {
    return n.toString().padStart(2, '0');
  }
}
